import React, { useEffect, useState } from "react";
import BookSuggestionManager from "../../services/dashboard/BookSuggestionManager";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileCsv, FaFileExcel, FaCheck, FaTimes, FaClock, FaSpinner } from "react-icons/fa";

const SuggestionManager = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ show: false, sugId: null, status: null, bookTitle: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const data = await BookSuggestionManager.getAllSuggestions();
      setSuggestions(data || []);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (sugId, status) => {
    setIsProcessing(true);
    try {
      await BookSuggestionManager.updateSuggestionStatus(sugId, status);
      setActionMessage(`✅ Suggestion ${status.toLowerCase()} successfully! Email notification sent to user.`);
      fetchSuggestions();
      setConfirmDialog({ show: false, sugId: null, status: null, bookTitle: "" });
      
      // Clear message after 5 seconds
      setTimeout(() => setActionMessage(""), 5000);
    } catch (err) {
      setActionMessage("❌ Failed to update status.");
      setTimeout(() => setActionMessage(""), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const showConfirmDialog = (sugId, status, bookTitle) => {
    setConfirmDialog({ show: true, sugId, status, bookTitle });
  };

  const closeConfirmDialog = () => {
    if (!isProcessing) {
      setConfirmDialog({ show: false, sugId: null, status: null, bookTitle: "" });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: FaClock,
        bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        textColor: "text-yellow-700 dark:text-yellow-400",
        borderColor: "border-yellow-200 dark:border-yellow-800",
        label: "Pending"
      },
      APPROVED: {
        icon: FaCheck,
        bgColor: "bg-green-50 dark:bg-green-900/20",
        textColor: "text-green-700 dark:text-green-400",
        borderColor: "border-green-200 dark:border-green-800",
        label: "Approved"
      },
      REJECTED: {
        icon: FaTimes,
        bgColor: "bg-red-50 dark:bg-red-900/20",
        textColor: "text-red-700 dark:text-red-400",
        borderColor: "border-red-200 dark:border-red-800",
        label: "Rejected"
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor} shadow-sm`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const renderActionButtons = (suggestion) => {
    if (suggestion.status === "PENDING") {
      return (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => showConfirmDialog(suggestion.sugId, "APPROVED", suggestion.title)}
            className="inline-flex items-center px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-all duration-150 ease-out shadow-sm hover:shadow-lg hover:scale-105 hover:-translate-y-0.5"
          >
            <FaCheck className="w-3 h-3 mr-1" />
            Approve
          </button>
          <button
            onClick={() => showConfirmDialog(suggestion.sugId, "REJECTED", suggestion.title)}
            className="inline-flex items-center px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-md transition-all duration-150 ease-out shadow-sm hover:shadow-lg hover:scale-105 hover:-translate-y-0.5"
          >
            <FaTimes className="w-3 h-3 mr-1" />
            Reject
          </button>
        </div>
      );
    } else {
      return (
        <span className="text-[10px] text-gray-400 dark:text-gray-500 italic font-normal opacity-60">
          Finalized
        </span>
      );
    }
  };

  /**
   * 1. Map each suggestion into a flat object with exactly the columns we want:
   *    ID, Title, Author, Edition, Message, Status, Created At
   */
  const mapSuggestionsForExport = () => {
    return suggestions.map((sug) => ({
      ID: sug.sugId,
      Title: sug.title || "",
      Author: sug.author || "",
      "First Name": sug.userFirstName || "",
      "Last Name": sug.userLastName || "",
      Edition: sug.edition || "",
      "Book Language": sug.bookLanguage || "",
      Message: sug.message || "",
      Status: sug.status || "",
      "Created At": sug.creationDate
        ? formatDateTimeFromArray(sug.creationDate)
        : "",
    }));
  };

  /**
   * 2. Export to CSV:
   *    - Create a worksheet from JSON
   *    - Convert to CSV text
   *    - Wrap in a Blob and trigger a download
   */
  const handleDownloadCSV = () => {
    const exportData = mapSuggestionsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Suggestions");

    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "book_suggestions.csv");
  };

  /**
   * 3. Export to Excel (.xlsx):
   *    - Create a worksheet from JSON
   *    - Write workbook as XLSX binary
   *    - Wrap in Blob and trigger a download
   */
  const handleDownloadExcel = () => {
    const exportData = mapSuggestionsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Suggestions");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "book_suggestions.xlsx");
  };

  const formatDateTimeFromArray = (arr) => {
    if (!Array.isArray(arr) || arr.length < 6) return "";
    
    // Same approach as AdminOrderRequests - create Date directly from array
    const dateTime = new Date(
      arr[0],        // year
      arr[1] - 1,    // month (0-indexed)
      arr[2],        // day
      arr[3] || 0,   // hour
      arr[4] || 0,   // minute
      arr[5] || 0    // second
    ).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
    
    return dateTime;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Book Suggestions
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Review and manage user book suggestions
            </p>
          </div>

          {/* Export Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <FaFileCsv className="w-4 h-4" />
              Download CSV
            </button>
            <button
              onClick={handleDownloadExcel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-blue-800 dark:text-blue-300 text-sm font-medium">{actionMessage}</p>
          </div>
        )}

        {/* Elite Table Container with Custom Scrollbar */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading suggestions...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
              <style>{`
                .scrollbar-thin::-webkit-scrollbar {
                  height: 8px;
                }
                .scrollbar-thin::-webkit-scrollbar-track {
                  background: transparent;
                }
                .scrollbar-thin::-webkit-scrollbar-thumb {
                  background-color: rgb(209 213 219);
                  border-radius: 4px;
                }
                .dark .scrollbar-thin::-webkit-scrollbar-thumb {
                  background-color: rgb(75 85 99);
                }
                .scrollbar-thin:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(156 163 175);
                }
                .dark .scrollbar-thin:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(107 114 128);
                }
              `}</style>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {/* Light Gray Header */}
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Author</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">First Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Last Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Edition</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Language</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                  </tr>
                </thead>
                
                {/* Elite Polished Table Body */}
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
                  {suggestions.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        No suggestions found.
                      </td>
                    </tr>
                  ) : (
                    suggestions.map((sug, index) => (
                      <tr
                        key={sug.sugId}
                        className={`transition-all duration-150 ease-out hover:bg-gray-100/60 dark:hover:bg-gray-900/60 cursor-pointer ${
                          index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/40 dark:bg-gray-900/20'
                        }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{sug.sugId}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-950 dark:text-white">{sug.title}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">{sug.author}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{sug.userFirstName || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{sug.userLastName || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{sug.edition || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{sug.bookLanguage || "-"}</td>
                        <td className="px-4 py-3 max-w-xs">
                          <div 
                            className="truncate text-sm text-gray-600 dark:text-gray-400"
                            title={sug.message}
                          >
                            {sug.message}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(sug.status)}
                        </td>
                        <td className="px-4 py-3">
                          {renderActionButtons(sug)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {sug.creationDate ? formatDateTimeFromArray(sug.creationDate) : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700">
                  {confirmDialog.status === "APPROVED" ? (
                    <FaCheck className="w-6 h-6 text-green-600" />
                ) : (
                  <FaTimes className="w-6 h-6 text-red-600" />
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                {confirmDialog.status === "APPROVED" ? "Approve Suggestion" : "Reject Suggestion"}
              </h3>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">
                Are you sure you want to {confirmDialog.status.toLowerCase()} the suggestion for{" "}
                <span className="font-medium text-gray-900 dark:text-white">"{confirmDialog.bookTitle}"</span>?
                <br />
                <span className="text-xs mt-2 block">
                  {confirmDialog.status === "APPROVED" 
                    ? "The user will receive an approval email notification."
                    : "The user will receive a rejection email notification."
                  }
                </span>
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={closeConfirmDialog}
                  disabled={isProcessing}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isProcessing 
                      ? "text-gray-400 bg-gray-50 cursor-not-allowed" 
                      : "text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleStatusChange(confirmDialog.sugId, confirmDialog.status)}
                  disabled={isProcessing}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors duration-200 flex items-center justify-center ${
                    confirmDialog.status === "APPROVED"
                      ? isProcessing 
                        ? "bg-green-400 cursor-not-allowed" 
                        : "bg-green-600 hover:bg-green-700"
                      : isProcessing 
                        ? "bg-red-400 cursor-not-allowed" 
                        : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    confirmDialog.status === "APPROVED" ? "Approve" : "Reject"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionManager;
